/**
 * iter71 — Studio Media Settings card block.
 *
 * Wraps `GET/PATCH /api/settings/media`. Three sub-cards:
 *   - Privacy (public vs private, notify-on-upload)
 *   - Downloads (master toggle, per-role resolution, ZIP toggle)
 *   - Watermark & Frames (preset selector, apply-on-viewing switch)
 *
 * All changes save to the same document. We call PATCH with only the
 * touched keys so unrelated fields never get clobbered by a stale UI state.
 */

import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/utils";
import { toast } from "sonner";
import {
  Bell, CheckCircle2, Download, FileImage, Globe2, Lock, Loader2,
  ShieldCheck, Sparkles, Stamp, Upload, Trash2, Zap,
} from "lucide-react";

const RESOLUTIONS = [
  { key: "original", label: "Original", hint: "Full resolution as uploaded" },
  { key: "2560", label: "2560 px JPG", hint: "Print & sharing quality" },
  { key: "1600", label: "1600 px JPG", hint: "Fast web viewing" },
];

const SIZES = [
  { key: "small",   label: "Small",   hint: "Elegant · 8%" },
  { key: "medium",  label: "Medium",  hint: "Balanced · 14%" },
  { key: "large",   label: "Large",   hint: "Strong · 22%" },
  { key: "xlarge",  label: "X-Large", hint: "Bold · 32%" },
];

// ------------------ Small primitives ------------------

function Toggle({ checked, onChange, testid, srLabel = "Toggle" }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={srLabel}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
        checked ? "bg-emerald-600" : "bg-stone-300"
      }`}
      data-testid={testid}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow ring-1 ring-stone-200 transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

function Row({ icon: Icon, title, description, right, testid }) {
  return (
    <div className="flex items-start gap-3 py-3" data-testid={testid}>
      {Icon && <Icon className="w-4 h-4 mt-0.5 text-stone-500" strokeWidth={1.7} />}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-stone-800">{title}</div>
        {description && <div className="text-xs text-stone-500 mt-0.5">{description}</div>}
      </div>
      <div className="pt-0.5">{right}</div>
    </div>
  );
}

function ResolutionGroup({ value, onChange, groupName, testid }) {
  return (
    <div className="grid grid-cols-3 gap-2" data-testid={testid}>
      {RESOLUTIONS.map((r) => {
        const active = value === r.key;
        return (
          <label
            key={r.key}
            className={`cursor-pointer rounded-lg border p-3 text-center transition-colors ${
              active ? "border-rose-500 bg-rose-50" : "border-stone-200 hover:bg-stone-50"
            }`}
          >
            <input
              type="radio"
              name={groupName}
              className="sr-only"
              value={r.key}
              checked={active}
              onChange={() => onChange(r.key)}
              data-testid={`${testid}-${r.key}`}
            />
            <div className={`text-xs font-semibold ${active ? "text-rose-800" : "text-stone-900"}`}>{r.label}</div>
            <div className="text-[10px] text-stone-500 mt-0.5">{r.hint}</div>
          </label>
        );
      })}
    </div>
  );
}

// ------------------ Main component ------------------

export default function MediaSettingsCard() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null); // full settings doc
  const [saving, setSaving] = useState(false);
  const [notifyRunning, setNotifyRunning] = useState(false);
  const [notifyResult, setNotifyResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/settings/media");
        setState(data);
      } catch (e) {
        setErr(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const patch = async (partial) => {
    setSaving(true);
    setErr("");
    try {
      const { data } = await api.patch("/settings/media", partial);
      setState(data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
      toast.error("Couldn't save — please retry");
    } finally {
      setSaving(false);
    }
  };

  const runNotify = async () => {
    setNotifyRunning(true);
    setNotifyResult(null);
    try {
      const { data } = await api.post("/settings/media/notify-waitlist");
      setNotifyResult(data);
      const msg = data.notified > 0
        ? `Notified ${data.notified} guest${data.notified === 1 ? "" : "s"}`
        : data.scanned === 0
          ? "No guests on the waitlist right now"
          : `Scanned ${data.scanned}, no new matches yet`;
      toast.success(msg);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setNotifyRunning(false);
    }
  };

  if (loading) {
    return (
      <section className="card p-6" data-testid="media-settings-loading">
        <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
        Loading media settings…
      </section>
    );
  }
  if (!state) {
    return (
      <section className="card p-6 text-sm text-red-700" data-testid="media-settings-error">
        Failed to load media settings. {err}
      </section>
    );
  }

  const s = state;

  return (
    <section className="space-y-6" data-testid="media-settings-card">
      {/* ============ Privacy ============ */}
      <div className="card p-6" data-testid="media-privacy-section">
        <div className="flex items-start gap-3">
          <ShieldCheck className="w-5 h-5 text-rose-700 mt-1" strokeWidth={1.6} />
          <div className="flex-1">
            <div className="editorial-eyebrow">Privacy</div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">Media privacy</h2>
            <p className="text-sm text-stone-500 mt-1">Choose who can see the media in your public galleries.</p>
          </div>
        </div>

        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <PrivacyCard
            active={s.privacy_mode === "public"}
            icon={Globe2}
            title="Public"
            subtitle="Past & newer uploads are public — guests see all public media through the gallery link."
            onSelect={() => patch({ privacy_mode: "public" })}
            testid="privacy-card-public"
          />
          <PrivacyCard
            active={s.privacy_mode === "private"}
            icon={Lock}
            title="Private"
            subtitle="Media visible only to featured guests or owners — hidden from the general public link."
            onSelect={() => patch({ privacy_mode: "private" })}
            testid="privacy-card-private"
          />
        </div>

        <div className="mt-5 border-t border-stone-200 pt-4">
          <Row
            icon={Bell}
            title="Notify users for new uploads"
            description="Automatically WhatsApp each waitlisted guest the moment a new photo of them lands."
            right={
              <Toggle
                checked={s.notify_on_upload}
                onChange={(v) => patch({ notify_on_upload: v })}
                testid="notify-on-upload-toggle"
                srLabel="Notify on upload"
              />
            }
            testid="row-notify-on-upload"
          />
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-3">
            <div className="text-xs text-stone-500 max-w-md">
              Run a one-off sweep across every guest on the waitlist right now. Great after a big upload batch.
            </div>
            <button
              type="button"
              onClick={runNotify}
              disabled={notifyRunning}
              className="btn btn-primary btn-sm inline-flex items-center gap-2"
              data-testid="notify-waitlist-run"
            >
              {notifyRunning ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
              {notifyRunning ? "Scanning…" : "Notify users for new uploads"}
            </button>
          </div>
          {notifyResult && (
            <div className="mt-3 text-[11px] text-stone-500 flex items-center gap-1.5" data-testid="notify-result">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
              Scanned {notifyResult.scanned} · Notified {notifyResult.notified} · Still waiting {notifyResult.still_waiting}
            </div>
          )}
        </div>
      </div>

      {/* ============ Downloads ============ */}
      <div className="card p-6" data-testid="media-downloads-section">
        <div className="flex items-start gap-3">
          <Download className="w-5 h-5 text-rose-700 mt-1" strokeWidth={1.6} />
          <div className="flex-1">
            <div className="editorial-eyebrow">Downloads</div>
            <h2 className="font-heading text-2xl font-semibold text-stone-900">Detailed download controls</h2>
            <p className="text-sm text-stone-500 mt-1">Fine-tune what your guests and clients can pull off the gallery.</p>
          </div>
        </div>

        <div className="mt-4 divide-y divide-stone-100">
          <Row
            icon={Zap}
            title="Allow downloads"
            description="Master switch — turns off every download button across the gallery."
            right={
              <Toggle
                checked={s.downloads.enabled}
                onChange={(v) => patch({ downloads: { ...s.downloads, enabled: v } })}
                testid="downloads-enabled-toggle"
                srLabel="Allow downloads"
              />
            }
            testid="row-downloads-enabled"
          />
        </div>

        <div className={`mt-5 space-y-5 ${s.downloads.enabled ? "" : "opacity-50 pointer-events-none"}`}>
          <div data-testid="owner-resolution-block">
            <div className="text-xs font-semibold uppercase tracking-wide text-stone-700">Owner / co-host / PIN viewers</div>
            <div className="text-xs text-stone-500 mb-2">Resolution served when the studio or featured guests download.</div>
            <ResolutionGroup
              value={s.downloads.owner_resolution}
              onChange={(v) => patch({ downloads: { ...s.downloads, owner_resolution: v } })}
              groupName="owner-res"
              testid="owner-resolution"
            />
          </div>
          <div data-testid="guest-resolution-block">
            <div className="text-xs font-semibold uppercase tracking-wide text-stone-700">Guests / public viewers</div>
            <div className="text-xs text-stone-500 mb-2">Resolution served to anyone with the public gallery link.</div>
            <ResolutionGroup
              value={s.downloads.guest_resolution}
              onChange={(v) => patch({ downloads: { ...s.downloads, guest_resolution: v } })}
              groupName="guest-res"
              testid="guest-resolution"
            />
          </div>

          <div className="border-t border-stone-100 pt-3">
            <Row
              icon={FileImage}
              title="Allow ZIP downloads"
              description="Guests can grab the whole album (or their favourites) in one archive."
              right={
                <Toggle
                  checked={s.downloads.allow_zip}
                  onChange={(v) => patch({ downloads: { ...s.downloads, allow_zip: v } })}
                  testid="allow-zip-toggle"
                  srLabel="Allow ZIP downloads"
                />
              }
              testid="row-allow-zip"
            />
          </div>
        </div>
      </div>

      {/* ============ Watermark ============ */}
      <WatermarkSection
        state={s}
        onPatch={(w) => patch({ watermark: { ...s.watermark, ...w } })}
        onLogoUploaded={(next) => setState(next)}
      />

      {saving && (
        <div className="fixed bottom-4 right-4 bg-stone-900 text-white text-xs px-3 py-2 rounded shadow-lg flex items-center gap-2 z-40" data-testid="media-saving-badge">
          <Loader2 className="w-3 h-3 animate-spin" /> Saving…
        </div>
      )}
      {err && (
        <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="media-settings-inline-error">
          {err}
        </div>
      )}
    </section>
  );
}

function PrivacyCard({ active, icon: Icon, title, subtitle, onSelect, testid }) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={`text-left rounded-xl border-2 p-4 transition-colors ${
        active ? "border-rose-600 bg-rose-50" : "border-stone-200 hover:bg-stone-50"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${active ? "text-rose-700" : "text-stone-500"}`} strokeWidth={1.6} />
        <div className="font-heading text-lg font-semibold text-stone-900">{title}</div>
        {active && (
          <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700">
            <CheckCircle2 className="w-3 h-3" /> Active
          </span>
        )}
      </div>
      <div className="mt-1.5 text-xs text-stone-600 leading-relaxed">{subtitle}</div>
    </button>
  );
}

// ============ iter198 — Watermark section ============
function WatermarkSection({ state, onPatch, onLogoUploaded }) {
  const w = state.watermark || {};
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [logoBust, setLogoBust] = useState(() => Date.now());
  const previewSrc = `${api.defaults.baseURL}/settings/media/watermark-logo?v=${logoBust}`;

  const handleUpload = async (file) => {
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("Logo must be under 4 MB"); return; }
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/settings/media/watermark-logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onLogoUploaded(data);
      setLogoBust(Date.now());
      toast.success("Watermark logo updated");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleClear = async () => {
    if (!window.confirm("Remove custom watermark logo? The studio branding logo will be used instead.")) return;
    try {
      const { data } = await api.delete("/settings/media/watermark-logo");
      onLogoUploaded(data);
      setLogoBust(Date.now());
      toast.success("Reverted to studio logo");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="card p-6" data-testid="media-watermark-section">
      <div className="flex items-start gap-3">
        <Stamp className="w-5 h-5 text-stone-800 mt-1" strokeWidth={1.6} />
        <div className="flex-1">
          <div className="editorial-eyebrow">Watermark</div>
          <h2 className="font-heading text-2xl font-semibold text-stone-900">Studio watermark</h2>
          <p className="text-sm text-stone-500 mt-1 max-w-xl">
            Applied consistently across every gallery — the same watermark shows while your clients view photos
            and is baked into individual downloads and ZIPs. Turn it off on a specific gallery from that gallery's Settings.
          </p>
        </div>
      </div>

      <div className="mt-5 border-t border-stone-100 pt-4">
        <Row
          icon={Stamp}
          title="Enable watermark by default"
          description="This becomes the studio-wide default. Individual galleries can override it."
          right={
            <Toggle
              checked={!!w.enabled}
              onChange={(v) => onPatch({ enabled: v })}
              testid="watermark-enabled-toggle"
              srLabel="Enable watermark"
            />
          }
          testid="row-watermark-enabled"
        />
      </div>

      <div className={`mt-5 grid gap-6 lg:grid-cols-[1fr_260px] ${w.enabled ? "" : "opacity-60 pointer-events-none"}`}>
        <div className="space-y-5">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-700">Watermark size</div>
            <div className="text-xs text-stone-500 mt-0.5">How large the logo appears in the corner of every photo.</div>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="watermark-size-group">
              {SIZES.map((s) => {
                const active = (w.size || "small") === s.key;
                return (
                  <button
                    key={s.key}
                    type="button"
                    onClick={() => onPatch({ size: s.key })}
                    className={`rounded-xl border p-3 text-left transition-colors ${
                      active ? "border-stone-900 bg-stone-900 text-white" : "border-stone-200 bg-white hover:bg-stone-50 text-stone-900"
                    }`}
                    data-testid={`watermark-size-${s.key}`}
                  >
                    <div className="text-sm font-semibold">{s.label}</div>
                    <div className={`text-[10px] mt-0.5 ${active ? "text-stone-300" : "text-stone-500"}`}>{s.hint}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.14em] text-stone-700">Watermark logo</div>
            <div className="text-xs text-stone-500 mt-0.5">
              Upload a custom logo (PNG recommended, max 4 MB). If you skip this, we use your studio logo automatically.
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="inline-flex items-center gap-2 rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-medium hover:bg-stone-50"
                data-testid="watermark-logo-upload-btn"
              >
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {w.custom_logo_key ? "Replace logo" : "Upload logo"}
              </button>
              {w.custom_logo_key && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm text-stone-600 hover:bg-red-50 hover:text-red-700"
                  data-testid="watermark-logo-clear-btn"
                >
                  <Trash2 className="w-4 h-4" /> Use studio logo
                </button>
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/webp,image/jpeg"
                onChange={(e) => handleUpload(e.target.files?.[0])}
                className="hidden"
                data-testid="watermark-logo-input"
              />
            </div>
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-stone-50 p-4 flex flex-col items-center justify-center" data-testid="watermark-logo-preview">
          <div className="text-[10px] uppercase tracking-[0.18em] text-stone-500 mb-3">Current logo</div>
          <div className="w-full aspect-square rounded-xl bg-white border border-stone-200 flex items-center justify-center overflow-hidden">
            <img
              key={logoBust}
              src={previewSrc}
              alt="Watermark preview"
              className="max-w-[80%] max-h-[80%] object-contain"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="text-[10px] text-stone-500 mt-3 text-center">
            {w.custom_logo_key ? "Custom logo" : "Studio logo (default)"}
          </div>
        </div>
      </div>
    </div>
  );
}
